/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { AbsenceDetailComponent } from '@absences/absence-detail/absence-detail.component';
import { AbsenceService } from '@absences/services/absence.service';
import * as AbsenceActions from '@absences/store/absences.actions';
import { getAbsenceOwner, getCurrentAbsence } from '@absences/store/absences.selectors';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LOGIN_SUCCESS } from '@auth/store/auth.actions';
import { isAuthenticated } from '@auth/store/auth.selectors';
import { State } from '@core/store';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AbsenceWrite } from '@shared/models/absence-write';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class AbsencesEffects {
  constructor(
    private actions$: Actions<AbsenceActions.AbsencesActions>,
    private translateService: TranslateService,
    private snackBarService: MatSnackBar,
    private dialogService: MatDialog,
    private store: Store<State>,
    private absenceService: AbsenceService
  ) { }


  init$ = createEffect(() => this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    withLatestFrom(this.store.select(isAuthenticated)),
    map(authenticated => {
      let classes = [];
      if (authenticated) {
        classes = JSON.parse(localStorage.getItem('absenceClasses'));
      }
      return new AbsenceActions.Init(classes);
    })
  ));


  loadAbsenceClasses$ = createEffect(() => this.actions$.pipe(
    ofType(LOGIN_SUCCESS, ROOT_EFFECTS_INIT),
    withLatestFrom(this.store.select(isAuthenticated)),
    filter(([_, authenticated]) => authenticated),
    switchMap(() => this.absenceService.getAbsenceClasses().pipe(
      tap(absenceClasses => localStorage.setItem('absenceClasses', JSON.stringify(absenceClasses))),
      switchMap(absenceClasses => of(new AbsenceActions.LoadAbsenceClassesSuccess(absenceClasses))),
      catchError(() => of(new AbsenceActions.LoadAbsenceClassesFailure()))
    ))
  ));


  editAbsence$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.EDIT_ABSENCE),
    tap(() => this.dialogService.open(AbsenceDetailComponent, {
      width: '25rem'
    }))
  ), { dispatch: false });


  loadAbsences$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.LOAD_ABSENCES),
    map(action => action.owner),
    switchMap(owner => this.absenceService.getAbsences({
      [owner.property]: owner.id
    }).pipe(
      map(absences => new AbsenceActions.LoadAbsencesSuccess(absences)),
      catchError(() => of(new AbsenceActions.LoadAbsencesFailure()))
    ))
  ));


  loadSelectableAbsenceClassesForOwner$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.LOAD_ABSENCES),
    map(action => new AbsenceActions.LoadSelectableAbsenceClasses(action.owner.property))
  ));


  loadSelectableAbsenceClasses$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.LOAD_SELECTABLE_ABSENCE_CLASSES),
    map(action => action.target),
    switchMap(target => this.absenceService.getAbsenceClasses(target).pipe(
      map(absenceClasses => new AbsenceActions.LoadSelectableAbsenceClassesSuccess(absenceClasses)),
      catchError(() => of(new AbsenceActions.LoadSelectableAbsenceClassesFailure()))
    ))
  ));


  loadAbsencesFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.LOAD_ABSENCES_FAILURE),
    switchMap(() => this.translateService.get('absences.errors.load').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });


  persistAbsence$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.PERSIST_ABSENCE),
    map(action => action.absence),
    withLatestFrom(this.store.select(getAbsenceOwner)),
    withLatestFrom(this.store.select(getCurrentAbsence)),
    switchMap(([[toPersist, owner], currentAbsence]) => {
      // Neues Objekt statt Mutation: toPersist ist ein Action-Payload und damit von
      // strictActionImmutability eingefroren.
      // standard_put ist aktiv: ein PUT ersetzt die Ressource komplett, das Ziel muss
      // also bei jedem Speichern mitgeschickt werden.
      const absence: AbsenceWrite = {
        ...toPersist,
        [owner.property]: owner.id,
        '@id': currentAbsence['@id']
      };

      const persistAbsence$ = absence['@id']
        ? this.absenceService.updateAbsence(absence)
        : this.absenceService.createAbsence(absence);

      return persistAbsence$.pipe(
        switchMap(() => [
          new AbsenceActions.PersistAbsenceSuccess(),
          new AbsenceActions.LoadAbsences(owner)
        ]),
        catchError(() => of(new AbsenceActions.PersistAbsenceFailure()))
      );
    })
  ));


  persistAbsenceSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.PERSIST_ABSENCE_SUCCESS),
    tap(() => this.dialogService.closeAll())
  ), { dispatch: false });


  persistAbsenceFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.PERSIST_ABSENCE_FAILURE),
    switchMap(() => this.translateService.get('absences.errors.persist').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });


  deleteAbsence$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.DELETE_ABSENCE),
    map(action => action.absenceId),
    switchMap(id => this.absenceService.deleteAbsence(id).pipe(
      withLatestFrom(this.store.select(getAbsenceOwner)),
      switchMap(([_, owner]) => [
        new AbsenceActions.DeleteAbsenceSuccess(),
        new AbsenceActions.LoadAbsences(owner)
      ]),
      catchError(() => of(new AbsenceActions.DeleteAbsenceFailure()))
    ))
  ));


  deleteAbsenceFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AbsenceActions.DELETE_ABSENCE_FAILURE),
    switchMap(() => this.translateService.get('absences.errors.delete').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });
}
